import {
  Paper,
  Toolbar,
  Typography,
  Collapse,
  IconButton,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import * as React from 'react';
import { ExternalOrderLog } from 'interfaces';
import { DelayedLinearProgress, SortableTable } from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { ExternalOrderService } from 'services';

const columns: SortableTableHeader[] = [
  { key: 'id', label: 'Log ID' },
  { key: 'received', label: 'Received' },
  { key: 'source', label: 'Source' },
  { key: 'external_order_id', label: 'External Order ID' },
  { key: 'status', label: 'Status' },
  { key: 'expand', sortable: false },
];

interface Props {
  shopId: number;
}

export const OrderLogView: React.FC<Props> = ({ shopId }) => {
  const [loading, setLoading] = React.useState(true);
  const [logs, setLogs] = React.useState<ExternalOrderLog[]>([]);
  const [expandedId, setExpandedId] = React.useState<number | null>(null);

  React.useEffect(() => {
    setLogs([]);
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const formatTimestamp = (value?: Date | string) => {
    if (!value) return '';
    const d = new Date(value as any);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
  };

  const prettyPayload = (raw: string) => {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  const rows = logs.flatMap((log) => {
    const isOpen = expandedId === log.id;
    const mainRow = {
      key: `log-${log.id}`,
      cells: [
        {
          key: 'id',
          display: <strong>{log.id}</strong>,
        },
        {
          key: 'received',
          display: formatTimestamp(log.receivedAt || log.created),
        },
        {
          key: 'source',
          display: log.externalOrderSource?.name ?? '—',
        },
        { key: 'external_order_id', display: log.externalOrderId },
        { key: 'status', display: log.status },
        {
          key: 'expand',
          display: (
            <IconButton
              size="small"
              onClick={() => setExpandedId(isOpen ? null : log.id)}
              aria-label={isOpen ? 'Hide payload' : 'Show payload'}
            >
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ),
        },
      ],
    };

    if (!isOpen) {
      return [mainRow];
    }

    const detailRow = {
      key: `log-${log.id}-detail`,
      cells: [
        {
          key: 'detail',
          display: (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {log.errorMessage && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{ marginBottom: 8 }}
                >
                  Error: {log.errorMessage}
                </Typography>
              )}
              <pre
                style={{
                  margin: 0,
                  padding: 12,
                  background: '#f5f5f5',
                  fontSize: 12,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {prettyPayload(log.rawPayload)}
              </pre>
            </Collapse>
          ),
          props: { colSpan: columns.length },
        },
      ],
    };

    return [mainRow, detailRow];
  });

  return (
    <React.Fragment>
      <DelayedLinearProgress loading={loading} />
      <Paper style={{ overflow: 'auto' }}>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Inbound order log</Typography>
        </Toolbar>
        <SortableTable
          columns={columns}
          rows={rows}
          emptyTableText="No inbound external orders yet."
          disablePagination
        />
      </Paper>
    </React.Fragment>
  );

  async function fetchLogs() {
    try {
      const response = await ExternalOrderService.getLogs({ shopId });
      setLogs(response);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }
};
